import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Switch } from "@headlessui/react";
import axios from "axios";
import { grid } from "ldrs";
grid.register();

const Step5 = ({ onNext }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [groupedFields, setGroupedFields] = useState({ CMF: {}, IFRS: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showIFRS, setShowIFRS] = useState(false);

  useEffect(() => {
    const fetchStep5Values = async () => {
      try {
        const response = await axios.get("/step5-values");
        if (response.data.status) {
          const fetchedFields = response.data.step4Values;

          const grouped = { CMF: {}, IFRS: {} };
          const initialFormData = {};

          fetchedFields.forEach((field) => {
            if (!grouped[field.standards][field.disclosureTopic]) {
              grouped[field.standards][field.disclosureTopic] = [];
            }
            grouped[field.standards][field.disclosureTopic].push(field);
            setShowIFRS(response.data.isIFRSCertified)

            // Store checked state
            initialFormData[field.id] = field.checked || false;
          });

          setGroupedFields(grouped);
          setFormData(initialFormData);
        }
      } catch (error) {
        console.error("Error fetching step 5 values:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStep5Values();
  }, []);

  const handleToggle = () => setShowIFRS(!showIFRS);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedIds = Object.keys(formData).filter((id) => {
      const isIFRSField = Object.values(groupedFields.IFRS).flat().some((field) => field.id.toString() === id);
      return formData[id] && (showIFRS || !isIFRSField);
    });

    if (selectedIds.length === 0) {
      alert("Please select at least one option.");
      return;
    }

    try {
      setSaving(true);
      await axios.post("/save-steps", { fieldId: selectedIds, stepNumber: 5,isIFRSCertified: showIFRS });
      onNext();
    } catch (error) {
      console.error("Error saving step 5 values:", error);
      alert("An error occurred while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <l-grid
          size="60"
          speed="1.5"
          color="#2F855A" // Tea Pink color
        ></l-grid>
        <p>{t('steps.loadingFields')}</p>
      </div>
    );
  }

  // Get unique disclosure topics
  const topics = Object.keys(groupedFields.CMF);

  return (
    <div className="container mx-auto mt-4 max-w-6xl bg-white shadow-md rounded-lg p-5">
      <h5 className="text-[30px] font-semibold leading-[110%] text-[#25324B] tracking-wide mb-4">
        {t('steps.selectField')}
      </h5>

      {/* Show IFRS Toggle Button */}
      <div className="mb-6 flex items-center gap-3">
        <Switch
          checked={showIFRS}
          onChange={handleToggle}
          className={`${showIFRS ? "bg-green-600" : "bg-gray-300"} relative inline-flex h-6 w-11 items-center rounded-full transition`}
        >
          <span className="sr-only">{t('steps.showIFRSFields')}</span>
          <span
            className={`inline-block h-4 w-4 transform bg-white rounded-full transition-transform ${showIFRS ? "translate-x-6" : "translate-x-1"}`}
          ></span>
        </Switch>
        <span className="text-lg font-medium text-gray-900">{t('steps.showIFRSFields')}</span>
      </div>

      {/* Table */}
      <form onSubmit={handleSubmit}>
        <table className="w-full border-collapse border border-gray-300 text-center">
          <thead>
            <tr>
              {topics.map((topic) => (
                <th key={topic} className="border border-gray-300 p-2 text-lg font-semibold">
                  {topic}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              let maxRows = 0;
              const distinctFieldsByTopic = {};

              topics.forEach((topic) => {
                const cmfValues = (groupedFields.CMF[topic] || []).filter((field) => formData[field.id]);
                let ifrsValues = (groupedFields.IFRS[topic] || []).filter((field) => formData[field.id]);
              
                // If toggle is OFF, do not show IFRS values
                if (!showIFRS) {
                  ifrsValues = [];
                }
              
                // Merge CMF and IFRS values, ensuring duplicates appear only once (in bold if from IFRS)
                const mergedValues = cmfValues.map((cmfField) => {
                  const matchingIFRS = ifrsValues.find((ifrsField) => ifrsField.disclosureField === cmfField.disclosureField);
                  return {
                    ...cmfField,
                    isBold: !!matchingIFRS, // Bold if matched with IFRS
                  };
                });
              
                // Filter out IFRS values that already exist in CMF
                const uniqueIfrsValues = ifrsValues.filter((ifrsField) => {
                  return !cmfValues.some((cmfField) => cmfField.disclosureField === ifrsField.disclosureField);
                });
              
                // Combine CMF with unique IFRS values
                distinctFieldsByTopic[topic] = [...mergedValues, ...uniqueIfrsValues];
                maxRows = Math.max(maxRows, distinctFieldsByTopic[topic].length);
              });
              

              return [...Array(maxRows)].map((_, rowIndex) => (
                <tr key={rowIndex}>
                  {topics.map((topic) => {
                    const values = distinctFieldsByTopic[topic] || [];
                    const field = values[rowIndex] || null;

                    return (
                      <td
                        key={topic}
                        className="border border-gray-300 p-2 text-center align-middle"
                      >
                        {field ? (
                          <div className="flex items-center justify-center gap-2">
                            <input
                              type="checkbox"
                              name={field.id.toString()}
                              checked={formData[field.id] || false}
                              onChange={handleCheckboxChange}
                              className="w-4 h-4 accent-green-600 bg-white border-gray-300 rounded-sm focus:ring-green-500 hidden"
                            />
                            <label
                              htmlFor={`field-${field.id}`}
                              className={`ms-2 text-sm text-gray-900 ${field.isBold ? "font-bold" : "font-normal"}`}
                            >
                              {field.disclosureField}
                            </label>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}
          </tbody>
        </table>

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="bg-green-600 text-white px-6 py-2 rounded-lg text-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
{saving ? t('answers.saving') : t('steps.next')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step5;